import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import DialogTemplate from 'components/shared/DialogTemplate';
import {
  Button,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Box,
} from '@material-ui/core';
import RangePicker from 'react-daterange-picker';
import {
  getAPIListFromCalendarRanges,
  getCalendarRangesFromAPIList,
  getNewCalendarRanges,
} from 'logic/bookingCalendarLogic';
import Alert from '@material-ui/lab/Alert';
import { validatePatched } from 'logic/cottage';
import moment from 'logic/moment';
import { useFormik } from 'components/shared/Formik';

const stateDefinitions = {
  contacttheowner: {
    state: 0,
    color: '#7ec3d8',
    label: <FormattedMessage id="Contact the owner" defaultMessage="Availability unknown" />,
  },
  unavailable: {
    state: 2,
    color: '#d0cece',
    label: <FormattedMessage id="Unavailable" defaultMessage="Unavailable" />,
  },
  booked: {
    state: 1,
    color: '#DE6567',
    label: <FormattedMessage id="Booked" defaultMessage="Booked" />,
  },
  enquire: {
    state: 3,
    color: '#fee27d',
    label: <FormattedMessage id="Preliminary booked" defaultMessage="Preliminary booked" />,
  },
  available: {
    state: 5,
    color: '#6cbd99',
    label: <FormattedMessage id="Available" defaultMessage="Available" />,
  },
};

const getNumberOfCalendars = (width) => {
  if (width >= 1341) {
    return 8;
  }

  if (width >= 1030) {
    return 6;
  }

  if (width > 660) {
    return 4;
  }

  return 1;
};

const styles = () =>
  makeStyles(() => ({
    disabled: {
      opacity: 0.6,
      pointerEvents: 'none',
    },
    flex: {
      display: 'flex',
    },
    maxWidth: {
      maxWidth: 320,
    },
    dialogSm: {
      margin: '20px',
      maxHeight: 'calc(100% - 10px)',
    },
  }));

const EditCalendar = ({ isLoading, onSubmit, onClose, cottage }) => {
  const theme = useTheme();
  const width = [...theme.breakpoints.keys].reduce((cur, key) => {
    return useMediaQuery(theme.breakpoints.up(key)) ? key : cur;
  }, 'xs');
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = styles();
  const intl = useIntl();
  const clickTimeout = React.useRef(null);
  const [dimensions, setDimensions] = React.useState({
    width: 0,
    height: 0,
  });
  const [selected, setSelected] = React.useState(null);
  const formik = useFormik({
    initialValues: {
      ...cottage,
    },
    enableReinitialize: true,
    validate: validatePatched({ intl, dialog: 'calendar' }),
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  React.useEffect(() => {
    const { innerHeight, innerWidth } = window;

    setDimensions({ width: innerWidth, height: innerHeight });
  }, [width]);

  React.useEffect(() => {
    return () => {
      clearTimeout(clickTimeout.current);
    };
  }, []);

  const cancel = () => setSelected(null);
  const handleSelect = (_selected) => {
    // Delay to prevent clicking to 'Choose' dialog
    clearTimeout(clickTimeout.current);
    clickTimeout.current = setTimeout(() => setSelected(_selected), 100);
  };

  const { values, handleSubmit, setFieldValue, dirty, submitForm } = formik;

  const markAs = (state) => {
    if (!selected) {
      return;
    }

    const dateRanges = getCalendarRangesFromAPIList(values.calendar || []);
    const newDateRanges = getNewCalendarRanges(dateRanges, selected, state);
    const calendar = getAPIListFromCalendarRanges(newDateRanges);

    setFieldValue('calendar', calendar);
    setSelected(null);
  };

  const dateStates = getCalendarRangesFromAPIList(values.calendar || []);
  const someFree = values.calendar ? values.calendar.some((d) => d.state === 5) : false;

  return (
    <DialogTemplate
      title={
        <>
          <FormattedMessage id="Calendar" defaultMessage="Calendar" />
          &nbsp;
          <>
            #{cottage?.id}
            {!isSmall && <>{cottage?.title_sv ? <> - "{cottage?.title_sv}"</> : ''}</>}
          </>
        </>
      }
      onSave={submitForm}
      pristine={!dirty}
      onClose={onClose}
      isUpdating={isLoading}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Box>
          <Alert severity="info" elevation={3}>
            <FormattedMessage
              id="cottages.calendar.help"
              defaultMessage="Feel free to check out our guide on how to update the calendar before you start, you can find it {link}"
              values={{
                link: (
                  <a
                    role="button"
                    tabIndex="0"
                    style={{ textDecoration: 'underline' }}
                    onClick={(e) => {
                      e.preventDefault();
                      window.supportHeroWidget.show({
                        article: '115956-hur-markerar-jag-tillgaenglighet-i-kalendern',
                      });
                    }}
                  >
                    <FormattedMessage id="here" defaultMessage="here" />
                  </a>
                ),
              }}
            />
          </Alert>
          <br />
          <Alert severity="warning" elevation={3}>
            <FormattedMessage
              id="cottages.calendar.info"
              defaultMessage="Important: If the visitor filters the search result by dates your ad will only appear if it is marked as free"
            />
          </Alert>
          <br />
          {!someFree && cottage.commission && (
            <>
              <Alert severity="error" elevation={3}>
                <FormattedMessage
                  id="cottages.calendar.nofree"
                  defaultMessage="Mark at least one period of the calendar as available"
                />
              </Alert>
              <br />
            </>
          )}
          <RangePicker
            showLegend
            dateStates={dateStates}
            defaultState="contacttheowner"
            firstOfWeek={1}
            numberOfCalendars={getNumberOfCalendars(dimensions.width, dimensions.height)}
            selectionType="range"
            stateDefinitions={stateDefinitions}
            selectedLabel={intl.formatMessage({
              id: 'Selected dates',
              defaultMessage: 'Selected dates',
            })}
            minimumDate={moment.utc().startOf('month')}
            maximumDate={moment.utc().add(5, 'years')}
            onSelect={handleSelect}
            locale={intl.locale === 'en' ? 'en-gb' : intl.locale}
            showWeekNumber
            weekNumberLabel={intl
              .formatMessage({ id: 'week', defaultMessage: 'week' })
              .charAt(0)
              .concat('.')}
          />
          <Typography variant="body2" component="div" align="center">
            <FormattedMessage
              id="cottages.calendar.sync"
              defaultMessage="Contact our support if you want to sync your calendar via ical links to any other system"
            />
          </Typography>
          <Dialog
            onClose={cancel}
            open={!!selected}
            classes={{ paperWidthXs: classes.dialogSm, paperWidthSm: classes.dialogSm }}
          >
            <DialogTitle>
              <FormattedMessage
                id="cottages.chooseState"
                defaultMessage="Choose state for date range"
              />
            </DialogTitle>
            <DialogContent className={classes.maxWidth}>
              <Typography>
                <FormattedMessage
                  id="cottages.youHaveSelected"
                  defaultMessage="You have selected the period {start} to {end},
                    how do you want to mark this period?"
                  values={{
                    start: selected && selected.start.format('LL'),
                    end: selected && selected.end.format('LL'),
                  }}
                />
              </Typography>
            </DialogContent>
            <List>
              {Object.keys(stateDefinitions).map((key) => (
                <ListItem
                  button
                  style={{ backgroundColor: stateDefinitions[key].color }}
                  key={key}
                  onClick={() => markAs(stateDefinitions[key].state)}
                >
                  <ListItemText primary={stateDefinitions[key].label} />
                </ListItem>
              ))}
            </List>
            <DialogActions>
              <Button onClick={cancel} color="primary">
                <FormattedMessage id="Cancel" defaultMessage="Cancel" />
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </form>
    </DialogTemplate>
  );
};

EditCalendar.propTypes = {
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cottage: PropTypes.shape({
    id: PropTypes.number,
    title_sv: PropTypes.string,
    commission: PropTypes.bool,
  }),
};

export default EditCalendar;
